import { useState, useEffect, useRef } from "react";

/**
 * Hook that ensures a loading state is shown for a minimum duration.
 * This provides better UX by giving visual feedback even for fast operations.
 *
 * @param isActuallyLoading - The actual loading state from the data source
 * @param minDurationMs - Minimum duration to show loading state (default: 800ms)
 * @returns boolean - Whether to show loading indicator
 */
const useMinimumLoadingDuration = (isActuallyLoading: boolean, minDurationMs = 800): boolean => {
  const [showLoading, setShowLoading] = useState(false);
  const loadingStartTime = useRef<number | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isActuallyLoading) {
      // Start loading - record the start time
      loadingStartTime.current = Date.now();
      setShowLoading(true);

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    } else if (loadingStartTime.current !== null) {
      // Loading finished - check if minimum duration has passed
      const elapsedTime = Date.now() - loadingStartTime.current;
      const remainingTime = minDurationMs - elapsedTime;

      if (remainingTime > 0) {
        // Need to wait for remaining time
        timeoutRef.current = setTimeout(() => {
          setShowLoading(false);
          loadingStartTime.current = null;
        }, remainingTime);
      } else {
        // Minimum duration already passed
        setShowLoading(false);
        loadingStartTime.current = null;
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isActuallyLoading, minDurationMs]);

  return showLoading;
};

export default useMinimumLoadingDuration;
